import { EpubCFI } from '../epub'
import type {
  BookMeta,
  Note,
  NoteComment,
  NoteCommentGroup,
  NotesGroup,
} from '../types/book'
import { logger } from './logger'

/** Merge by note identity, preserving deletions even when old aliases reappear. */
export const mergeNotes = (
  secondary: NotesGroup = {},
  primary: NotesGroup = {},
): NotesGroup => {
  const merged: NotesGroup = {}
  const aliasKeys: string[] = []
  const suppressedLiveIds = new Set<string>()

  for (const group of [secondary, primary]) {
    // Prefer the canonical entry's content over a legacy alias in each group.
    const entries = Object.entries(group).sort(
      ([a, left], [b, right]) => Number(a === left.id) - Number(b === right.id),
    )
    for (const [key, note] of entries) {
      const id = note.id || key
      if (key !== id) aliasKeys.push(key)
      const existing = merged[id]
      if (!existing) {
        merged[id] = note
        continue
      }
      if (!!existing.deleted !== !!note.deleted) suppressedLiveIds.add(id)
      merged[id] = {
        ...existing,
        ...note,
        comments: { ...existing.comments, ...note.comments },
        ...(existing.deleted || note.deleted ? { deleted: true } : {}),
      }
    }
  }

  if (aliasKeys.length || suppressedLiveIds.size) {
    logger.debug('[note-sync] reconciled legacy aliases and deletions', {
      aliasCount: aliasKeys.length,
      aliasKeys: aliasKeys.slice(0, 10),
      suppressedLiveCount: suppressedLiveIds.size,
      suppressedLiveIds: [...suppressedLiveIds].slice(0, 10),
      resultCount: Object.keys(merged).length,
    })
  }
  return merged
}

/**
 * Gets the start and end points of a CFI
 * @param cfi The CFI to get endpoints for
 * @returns An array with two elements: [start, end]
 */
const getEndpoints = (cfi: EpubCFI): [EpubCFI, EpubCFI] => {
  if (cfi.range) {
    const start = cfi
    const end = new EpubCFI(cfi.toString())
    start.collapse(true)
    end.collapse(false)
    return [start, end]
  }
  return [cfi, cfi]
}

/**
 * Checks if two CFIs overlap
 * @param cfi1 The first CFI (can be a range or a point)
 * @param cfi2 The second CFI (can be a range or a point)
 * @returns True if the CFIs overlap, false otherwise
 */
const cfiOverlap = (cfi1: string, cfi2: string): boolean => {
  const epubCfi1 = new EpubCFI(cfi1)
  const epubCfi2 = new EpubCFI(cfi2)

  const [start1, end1] = getEndpoints(epubCfi1)
  const [start2, end2] = getEndpoints(epubCfi2)

  return !(
    EpubCFI.prototype.compare(end1, start2) < 0 ||
    EpubCFI.prototype.compare(start1, end2) > 0
  )
}

/**
 * Filters a list of CFIs to only those that overlap with a target CFI
 * @param targetCFI The CFI to check for overlap
 * @param cfiList The list of CFIs to filter
 * @returns An array of CFIs that overlap with the target CFI
 */
// const filterOverlappingCFIs = (targetCFI: string, cfiList: string[]): string[] => {
//   return cfiList.filter((cfi) => cfiOverlap(targetCFI, cfi));
// };

/**
 * Gets all notes that overlap with a target CFI
 * @param targetCFI The CFI to check for overlap
 * @param notes The group of notes to check
 * @returns An object containing overlapping notes, with note IDs as keys
 */
const getNotesEnclosingCFI = (
  targetCFI: string,
  notes: NotesGroup,
): NotesGroup => {
  return Object.fromEntries(
    Object.entries(notes).filter(([_id, note]) =>
      cfiOverlap(targetCFI, note.cfiRange),
    ),
  )
}

/**
 * Filters out deleted items from a group of notes or note comments.
 *
 * @template T - The type of the group (NotesGroup or NoteCommentGroup)
 * @param group - The group of notes or note comments to filter.
 * @returns A new object of the same type as the input, containing only the non-deleted items.
 */
const filterDeleted = <T extends NotesGroup | NoteCommentGroup>(
  group: T | undefined,
): T => {
  if (!group) {
    return {} as T
  }

  return Object.fromEntries(
    Object.entries(group).filter(([_, item]) => !item.deleted),
  ) as T
}

/**
 * Counts the number of non-deleted comments for a given note.
 *
 * @param note - The note object containing comments to count.
 * @returns The number of non-deleted comments in the note.
 */
const countComments = (note: Note): number => {
  return Object.keys(filterDeleted(note.comments)).length
}

export const getLatestTimestamp = (note: Note): string => {
  const commentTimestamps = note.comments
    ? Object.values(note.comments)
        .filter((comment) => !comment.deleted && comment.timestamp)
        .map((comment) => comment.timestamp)
    : []
  return (
    [note.timestamp, ...commentTimestamps].filter(Boolean).sort().pop() ||
    note.timestamp
  )
}

const getLatestTimestampMs = (note: Note): number => {
  const parsedTimestamp = Date.parse(getLatestTimestamp(note) || '')
  return Number.isFinite(parsedTimestamp) ? parsedTimestamp : 0
}

const getNoteProgress = (note: Note): number | null => {
  if (typeof note.progress !== 'number' || !Number.isFinite(note.progress)) {
    return null
  }
  return note.progress
}

const compareCfiValues = (
  leftCfi: string,
  rightCfi: string,
  source: string,
): number => {
  try {
    return EpubCFI.prototype.compare(leftCfi, rightCfi)
  } catch (error) {
    logger.warn('notes: failed to compare CFIs', {
      source,
      leftCfi,
      rightCfi,
      error,
    })
    return 0
  }
}

const compareNotesByCfi = (a: Note, b: Note): number =>
  compareCfiValues(a.cfiRange, b.cfiRange, 'compareNotesByCfi')

const isNoteBeforeOrAtLocation = (
  note: Note,
  currentLocation: string,
): boolean =>
  compareCfiValues(
    note.cfiRange,
    currentLocation,
    'isNoteBeforeOrAtLocation',
  ) !== 1

export const compareNotesBySpace = (a: Note, b: Note): number => {
  const progressA = getNoteProgress(a)
  const progressB = getNoteProgress(b)
  if (progressA !== null && progressB !== null && progressA !== progressB) {
    return progressA - progressB
  }

  const cfiComparison = compareNotesByCfi(a, b)
  if (cfiComparison !== 0) {
    return cfiComparison
  }

  if (progressA !== null && progressB !== null) {
    return progressA - progressB
  }

  return getLatestTimestampMs(b) - getLatestTimestampMs(a)
}

export const compareNotesByTime = (a: Note, b: Note): number => {
  const timestampDiff = getLatestTimestampMs(b) - getLatestTimestampMs(a)
  if (timestampDiff !== 0) {
    return timestampDiff
  }

  return compareNotesBySpace(a, b)
}

export const buildVisibleSortedNotes = ({
  notesById,
  currentLocation,
  showNotes,
  sortByTime,
}: {
  notesById?: NotesGroup
  currentLocation?: string
  showNotes?: boolean
  sortByTime: boolean
}): { hiddenCount: number; notes: Note[] } => {
  const activeNotes = Object.values(notesById || {}).filter(
    (note) => !note.deleted,
  )
  const filteredNotes =
    currentLocation && !showNotes
      ? activeNotes.filter((note) =>
          isNoteBeforeOrAtLocation(note, currentLocation),
        )
      : activeNotes
  const comparator = sortByTime ? compareNotesByTime : compareNotesBySpace

  return {
    hiddenCount: activeNotes.length - filteredNotes.length,
    notes: [...filteredNotes].sort(comparator),
  }
}

export const buildNoteDeletionBooks = (
  currentBook: BookMeta,
  note: Note,
): {
  displayBook: BookMeta
  persistedBook: BookMeta
  tombstone: Note
} => {
  const currentNotes = mergeNotes({}, currentBook.notes)
  const currentNote = currentNotes[note.id] || note
  const tombstone: Note = {
    ...currentNote,
    ...note,
    deleted: true,
  }
  const persistedNotes: NotesGroup = {
    ...currentNotes,
    [note.id]: tombstone,
  }
  const displayNotes: NotesGroup = {
    ...persistedNotes,
  }

  delete displayNotes[note.id]

  return {
    persistedBook: {
      ...currentBook,
      notes: persistedNotes,
    },
    displayBook: {
      ...currentBook,
      notes: displayNotes,
    },
    tombstone,
  }
}

/**
 * Gets the first non-deleted comment on a note, or undefined if there are none.
 *
 * @param note - The note object containing comments to search.
 * @returns The first non-deleted comment, or undefined if there are no non-deleted comments.
 */
const getFirstComment = (note: Note): NoteComment | undefined => {
  const nonDeletedComments = filterDeleted(note.comments)
  const firstCommentKey = Object.keys(nonDeletedComments)[0]
  return firstCommentKey ? nonDeletedComments[firstCommentKey] : undefined
}

export { getNotesEnclosingCFI, filterDeleted, countComments, getFirstComment }
